import React, { useState, useEffect } from 'react';
import { Mail, Phone } from 'lucide-react';
import sanityClient from '../lib/sanityClient';
import SEO from '../components/SEO';

function CoachesPage() {
  const [coaches, setCoaches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "coach" && active == true] | order(order asc) {
          _id,
          name,
          position,
          "imageUrl": image.asset->url,
          email,
          phone,
          bio,
          certifications,
          yearsWithTeam,
          previousTeams,
          slug
        }`
      )
      .then((data) => {
        setCoaches(data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching coaches:', err);
        setError('Failed to load coaching staff. Please try again later.');
        setIsLoading(false);
      });
  }, []);

  if (isLoading) {
    return (
      <>
        <SEO
          title={'Coaches - Bombardiers Hockey News'}
          description={'The coaches of the Bombardiers Hockey team.'}
        />
        <div className="bg-black min-h-screen flex justify-center items-center">
          <div className="text-white text-xl">Loading coaching staff...</div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SEO
          title={'Coaches - Bombardiers Hockey News'}
          description={'The coaches of the Bombardiers Hockey team.'}
        />
        <div className="bg-black min-h-screen flex justify-center items-center">
          <div className="bg-gray-900 p-6 rounded-lg text-white text-xl">{error}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={'Coaches - Bombardiers Hockey News'}
        description={'The coaches of the Bombardiers Hockey team.'}
      />
      <div className="bg-black min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-12">Coaching Staff</h1>

          {/* Unified Grid Layout for All Coaches */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {coaches.map((coach) => (
              <div
                key={coach._id}
                className="bg-gray-900 rounded-lg overflow-hidden border border-gray-800 hover:border-yellow-600 transition-all group relative"
              >
                {/* Coach Image */}
                <div className="aspect-[4/5] bg-gray-800 relative overflow-hidden">
                  <img
                    src={coach.imageUrl || '/coach-placeholder.png'}
                    alt={coach.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.currentTarget.src = '/coach-placeholder.png';
                    }}
                  />

                  {/* Position Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-4">
                    <div className="text-yellow-500 font-bold text-lg mb-1">{coach.position}</div>
                    <div className="text-white font-bold text-xl">{coach.name}</div>
                  </div>
                </div>

                {/* Coach Details */}
                <div className="p-4">
                  {/* Bio */}
                  {coach.bio && (
                    <p className="text-gray-300 text-sm mb-4 line-clamp-3">{coach.bio}</p>
                  )}

                  {/* Years with Team */}
                  {coach.yearsWithTeam && (
                    <div className="mb-3">
                      <span className="text-gray-400 text-sm">Years with team:</span>
                      <span className="text-white text-sm ml-1">{coach.yearsWithTeam}</span>
                    </div>
                  )}

                  {/* Certifications */}
                  {coach.certifications && coach.certifications.length > 0 && (
                    <div className="mb-4">
                      <div className="text-gray-400 text-sm mb-2">Certifications:</div>
                      <div className="flex flex-wrap gap-1">
                        {coach.certifications.slice(0, 2).map((cert, index) => (
                          <span
                            key={index}
                            className="bg-gray-800 text-gray-300 px-2 py-1 rounded-full text-xs"
                          >
                            {cert}
                          </span>
                        ))}
                        {coach.certifications.length > 2 && (
                          <span className="bg-gray-800 text-gray-300 px-2 py-1 rounded-full text-xs">
                            +{coach.certifications.length - 2} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Previous Teams */}
                  {coach.previousTeams && coach.previousTeams.length > 0 && (
                    <div className="mb-4">
                      <div className="text-gray-400 text-sm mb-1">Previous teams:</div>
                      <div className="text-gray-300 text-sm">
                        {coach.previousTeams.slice(0, 2).join(', ')}
                        {coach.previousTeams.length > 2 && '...'}
                      </div>
                    </div>
                  )}

                  {/* Contact Information */}
                  <div className="flex flex-wrap gap-3 pt-3 border-t border-gray-800">
                    {coach.email && (
                      <a
                        href={`mailto:${coach.email}`}
                        className="flex items-center text-yellow-500 hover:text-yellow-400 text-sm"
                      >
                        <Mail className="h-4 w-4 mr-1" />
                        Email
                      </a>
                    )}
                    {coach.phone && (
                      <a
                        href={`tel:${coach.phone}`}
                        className="flex items-center text-yellow-500 hover:text-yellow-400 text-sm"
                      >
                        <Phone className="h-4 w-4 mr-1" />
                        Phone
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {coaches.length === 0 && (
            <div className="bg-gray-900 p-8 rounded-lg text-center">
              <p className="text-gray-400 text-lg">No coaching staff information available.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default CoachesPage;
